import { Helmet } from 'react-helmet';
import { PageSEO } from '@/data/seoData';

interface SEOHeadProps {
  seo: PageSEO;
  path?: string;
  noIndex?: boolean;
}

export const SEOHead = ({ seo, path = '', noIndex = false }: SEOHeadProps) => {
  const siteUrl = 'https://simnetwireless.com';
  const canonicalUrl = `${siteUrl}${path}`;
  const ogImageUrl = seo.ogImage
    ? `${siteUrl}${seo.ogImage}`
    : `${siteUrl}/images/hero-field-tech.jpg`;
  const twitterTitle = seo.ogTitle || seo.title;
  const twitterDescription = seo.ogDescription || seo.description;

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "SimNet Wireless",
    "description": "Field Communications & Connectivity Platform for contractors and field service businesses. Business internet, failover, fleet tracking, field tablets with push-to-talk, jobsite connectivity, and more.",
    "url": "https://simnetwireless.com",
    "telephone": "512-859-5047",
    "email": "sales@simnetwireless.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Dallas",
      "addressRegion": "TX",
      "addressCountry": "US"
    },
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 32.7767,
        "longitude": -96.7970
      },
      "geoRadius": "800km"
    }
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seo.title}</title>
      <meta name="title" content={seo.title} />
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords.join(', ')} />
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* LLM Context Links */}
      <link rel="llm-context" href={`${siteUrl}/llm.txt`} />
      <meta name="llm-context" content={`${siteUrl}/llm.txt`} />

      {/* Open Graph / Facebook / LinkedIn */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={seo.ogTitle || seo.title} />
      <meta property="og:description" content={seo.ogDescription || seo.description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:site_name" content="SimNet Wireless" />

      {/* Twitter */}
      <meta name="twitter:card" content={seo.twitterCard || "summary_large_image"} />
      <meta name="twitter:title" content={twitterTitle} />
      <meta name="twitter:description" content={twitterDescription} />
      <meta name="twitter:image" content={ogImageUrl} />

      <link rel="me" href="https://www.linkedin.com/company/simnet-wireless-llc/" />

      {/* Site-wide LocalBusiness JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
    </Helmet>
  );
};
